/**
 * DynamicPrompts component renders dynamic AI-generated prompts.
 *
 * @module views/components/DynamicPrompts
 * @memberof -Common
 */
import './DynamicPrompts.scss';

import React, { useEffect, useState } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Text as PalText } from 'web-palette/dist/components/Text/Text';

import Icon from '@ulta/core/components/Icon/Icon';
import { useListenForGlobal } from '@ulta/core/hooks/useListenForGlobal/useListenForGlobal';
import datacapture, { patchDataLayer } from '@ulta/core/utils/datacapture/datacapture';
import { getShoppingAgentTokenCache } from '@ulta/core/utils/shoppingAgentSession/shoppingAgentSession';

import Titles from '@ulta/modules/Titles/Titles';

import JSscriptTag from '../JSscriptTag/JSscriptTag';

const PROMPTS_UNAVAILABLE_EVENT = 'google-agent-pdp-prompts-unavailable';
const PROMPTS_LOADED_EVENT = 'google-agent-pdp-prompts-loaded';
const GOOGLE_AGENT_QUERY_SENT = 'google-agent-query-sent';
const PROMPT_CLICKED = 'google-agent-pdp-prompt-clicked';

/**
 * Represents a DynamicPrompts component
 *
 * @method
 * @param {DynamicPromptsProps} props - React properties passed from composition
 * @returns DynamicPrompts
 */
export const DynamicPrompts = function( props ){
  const {
    direction = 'horizontal',
    limit = 3,
    pageUrl,
    scriptConfig,
    title,
    subtitle,
    icon,
    id,
    poweredByUltaIcon,
    poweredByUltaText,
    backgroundColor,
    backgroundColorGradient,
    promptClickAction,
    accessibilityLabel
  } = props;

  const [hasPromptsLoaded, setIsPromptsLoaded] = useState( false );
  const [canLoadScript, setCanLoadScript] = useState( false );

  useListenForGlobal( {
    watch: () => !!getShoppingAgentTokenCache()?.sessionId,
    callback: () => setCanLoadScript( true )
  } );

  useEffect( () => {
    if( typeof window !== 'undefined' && pageUrl ){
      window.googleAgentSettings = {
        disablePrompts: false,
        pageUrl: pageUrl
      };
    }

    const handleUnavailable = () => {
      setIsPromptsLoaded( false );
    };

    const handleLoaded = () => {
      setIsPromptsLoaded( true );
    };

    const handlePromptClicked = ( e ) => {

      const promptValue = e?.detail?.prompt;

      if( !promptValue ){
        return;
      }

      const patchedData = patchDataLayer( {
        dataCaptureData: promptClickAction?.dataCaptureData,
        values: { selectedDynamicPrompt: promptValue }
      } );
      datacapture.processDataCaptureEvent( patchedData );
    };

    window.addEventListener( PROMPTS_UNAVAILABLE_EVENT, handleUnavailable );
    window.addEventListener( PROMPTS_LOADED_EVENT, handleLoaded );
    window.addEventListener( PROMPT_CLICKED, handlePromptClicked );

    return () => {
      window.removeEventListener( PROMPTS_UNAVAILABLE_EVENT, handleUnavailable );
      window.removeEventListener( PROMPTS_LOADED_EVENT, handleLoaded );
      window.removeEventListener( PROMPT_CLICKED, handlePromptClicked );
    };
  }, [pageUrl] );

  if( !pageUrl ){
    return null;
  }

  return (
    <>
      { canLoadScript && scriptConfig?.src && (
        <JSscriptTag
          type='text/javascript'
          tagId='google-ai-prompts-script'
          src={ scriptConfig.src }
          async={ scriptConfig.async ?? true }
          defer={ scriptConfig.defer ?? true }
        />
      ) }

      { /*
        We MUST keep <google-ai-prompts> in the DOM at all times so the external script
        can find it and dispatch the 'loaded' or 'unavailable' events.
        Using display: none prevents it from blinking/taking up space until the script is ready.
      */ }
      <div
        id={ id }
        className='DynamicPrompts'
        style={ { display: hasPromptsLoaded ? 'block' : 'none' } }
        role='region' aria-label={ accessibilityLabel }
      >
        <div className='DynamicPrompts__container' style={ { '--bg-gradient': `var(${ backgroundColorGradient })`, '--bg-dynamic-prompts': `var(${ backgroundColor })` } }>

          <div className='DynamicPrompts__header'>
            <Titles
              headline={ title }
              size='compact'
              subheadline={ subtitle }
              textAlignment='left'
              variant='section'
              iconName={ icon }
              iconSize='compact'
            />
          </div>

          <div className='DynamicPrompts__content'>
            { /* Custom Web Component handled by Google's static script */ }
            <google-ai-prompts
              direction={ direction }
              limit={ limit }
            />
          </div>
          <div className='DynamicPrompts__poweredByUlta'>
            <Icon
              ariaHidden
              className='testClassName'
              description='test icon description'
              name={ poweredByUltaIcon }
              size='lg'
              title='test title'
            />
            <PalText
              htmlTag='span'
              textAlign='left'
              textColor='content-default'
              textStyle='body-sm'
            >
              { poweredByUltaText }
            </PalText>


          </div>
        </div>
      </div>
    </>
  );
};

/**
 * Property type definitions
 *
 * @typedef {object} DynamicPromptsProps
 * @property {string} [direction='horizontal'] - Layout orientation of prompt chips ('horizontal', 'vertical')
 * @property {number} [limit=3] - Maximum number of prompts to display
 * @property {string} pageUrl - The production PDP URL passed to Google's API
 * @property {object} scriptConfig - JSscriptTag configuration
 * @property {string} title - Optional title to display above prompts
 * @property {string} subtitle - Optional subtitle to display below title
 * @property {object} backgroundImage - Optional background image asset
 * @property {string} icon - Optional icon URL
 * @property {string} id - Component ID
 * @property {string} [poweredByUltaIcon] - Icon to display next to the powered by text
 * @property {string} [poweredByUltaText] - Powered by text
 * @property {string} [backgroundColor] - Background color of the container
 * @property {string} [backgroundColorGradient] - Background color gradient of the container
 * @property {object} [promptClickAction] - Action to perform on prompt click
 */
export const propTypes = {
  direction: PropTypes.oneOf( ['horizontal', 'vertical'] ),
  limit: PropTypes.number,
  pageUrl: PropTypes.string,
  scriptConfig: PropTypes.shape( {
    src: PropTypes.string,
    async: PropTypes.bool,
    defer: PropTypes.bool
  } ),
  title: PropTypes.string,
  subtitle: PropTypes.string,
  backgroundImage: PropTypes.shape( {
    imageUrl: PropTypes.string,
    altText: PropTypes.string,
    metaData: PropTypes.object
  } ),
  icon: PropTypes.string,
  id: PropTypes.string,
  poweredByUltaIcon: PropTypes.string,
  poweredByUltaText: PropTypes.string,
  backgroundColor: PropTypes.string,
  backgroundColorGradient: PropTypes.string,
  promptClickAction: PropTypes.shape( {
    dataCaptureData: PropTypes.object
  } )
};

DynamicPrompts.propTypes = propTypes;

export default DynamicPrompts;
